import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel, Tooltip, Theme, tableCellClasses, TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  CircularProgress,
  Box
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useHistory } from 'react-router'
import { BASE_PATH } from '../../../config/envConfig'
import DropdownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { UserLoginSortOrder } from '@professocialApi/professocial-core-frontend-api'
import { HiddenAttr } from '../../../screens/Admin/AdminAccounts'
import useHidden from '../../../util/useHidden'
import { makeStyles } from '@mui/styles'

interface NetworkPagesResponse {
  facebookPages: FacebookPage[]
  instagramPages: InstagramPage[]
  googlePages: GooglePage[]
}

interface FacebookPage {
  networkPageId: string
  active: boolean
  firstConnectedAt: string
  lastConnectedAt: string
  networkPageName: string
  pageTokenValid: boolean
}

interface InstagramPage {
  networkPageId: string
  active: boolean
  firstConnectedAt: string
  lastConnectedAt: string
  networkPageName: string
  pageTokenValid: boolean
}

interface GooglePage {
  networkPageId: string
  networkPageName: string
  storeCode: string
}

type PageTableTemplate = {
  facebook: PageTemplate[],
  instagram: PageTemplate[],
  google: PageTemplate[],
}

type PageTemplate = {
  name: string
  hidden?: HiddenAttr
  sortable?: PageSortValue
  label?: string
  format?: string
}

enum PageSortValue {
  NetworkPageId = 'networkPageId',
  Active = 'active',
  FirstConnectedAt = 'firstConnectedAt',
  LastConnectedAt = 'lastConnectedAt',
  NetworkPageName = 'networkPageName',
  PageTokenValid = 'pageTokenValid'
}

type SortOrder = 'asc' | 'desc'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    marginBottom: theme.spacing(7),
  },
  tableContainer: {
    marginTop: theme.spacing(1),
  },
  table: {
    width: '100%',
    overflowX: 'auto',
  },
  tableHeadCell: {
    '&:first-child': {
      paddingLeft: '24px',
    },
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: 'white',
      fontWeight: 'bold',
    },
  },
  tableBodyCell: {
    '&:first-child': {
      paddingLeft: '24px',
    },
  },
  sortLabel: {
    fontWeight: 'bold',
  },
  IconBox: {
    display: 'flex',
  },
  noAccounts: {
    padding: theme.spacing(2),
    width: '100%',
    textAlign: 'center',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

const UnlinkedPagesTable: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const classes = useStyles()
  const isVisible = useHidden()

  const [networkPagesData, setNetworkPagesData] = useState<NetworkPagesResponse | null>(null)
  const [selectedNetwork, setSelectedNetwork] = useState<keyof PageTableTemplate>('facebook')
  const [order, setOrder] = useState<UserLoginSortOrder>(UserLoginSortOrder.Asc)
  const [orderBy, setOrderBy] = useState<PageSortValue>(PageSortValue.NetworkPageName)
  const [sortColumn, setSortColumn] = useState<string>('networkPageName')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [pageToDelete, setPageToDelete] = useState<{ name: string; id: string } | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const requestHeaders: HeadersInit = new Headers()
  const xsrfToken = sessionStorage.getItem('XSRF-TOKEN')
  if (xsrfToken) requestHeaders.set('X-XSRF-TOKEN', xsrfToken)
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')

  // Function to fetch network pages data
  const fetchNetworkPagesData = () => {
    setIsLoading(true)
    const restUrl = `${BASE_PATH}/rest/professocial/core/admin/pages/unlinked`
    fetch(restUrl, {
      method: 'GET',
      credentials: 'include',
      headers: requestHeaders,
    })
      .then((res) => res.json())
      .then((json: NetworkPagesResponse) => {
        setNetworkPagesData(json)
      })
      .catch((error) => {
        console.error('Error fetching network pages data:', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // Fetch data when component mounts
  useEffect(() => {
    fetchNetworkPagesData()
  }, [])

  // Handle network selection change
  const handleNetworkChange = (dropDownElement: DropDownElement) => {
    setSelectedNetwork(dropDownElement.key as keyof PageTableTemplate)
  }

  // Extract the pages based on the selected network
  const pages = selectedNetwork === 'facebook' ? networkPagesData?.facebookPages
    : selectedNetwork === 'instagram' ? networkPagesData?.instagramPages
      : networkPagesData?.googlePages

  const filterOptions: DropDownElement[] = [
    {
      key: 'facebook',
      value: t('accountsCommon:facebook'),
    },
    {
      key: 'instagram',
      value: t('accountsCommon:instagram'),
    },
    {
      key: 'google',
      value: t('accountsCommon:google'),
    },
  ]

  const defaultDateFormat = 'dd.MM.yyyy'
  const defaultTimeFormat = 'HH:mm'

  const pageTableTemplate: PageTableTemplate = {
    facebook: [
      {
        name: 'networkPageName',
        hidden: undefined,
        sortable: PageSortValue.NetworkPageName,
        label: 'networkPageName',
      },
      {
        name: 'networkPageId',
        hidden: undefined,
        sortable: PageSortValue.NetworkPageId,
        label: 'networkPageId',
      },
      {
        name: 'active',
        hidden: HiddenAttr.mdUp,
        sortable: PageSortValue.Active,
        label: 'active',
      },
      {
        name: 'firstConnectedAt',
        hidden: HiddenAttr.mdUp,
        sortable: PageSortValue.FirstConnectedAt,
        label: 'firstConnectedAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastConnectedAt',
        hidden: HiddenAttr.mdUp,
        sortable: PageSortValue.LastConnectedAt,
        label: 'lastConnectedAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'pageTokenValid',
        hidden: HiddenAttr.lgUp,
        sortable: PageSortValue.PageTokenValid,
        label: 'pageTokenValid',
      },
      {
        name: 'deleteAction',
        hidden: undefined,
      },
      {
        name: 'urlToPage',
        hidden: undefined,
        label: 'urlToPage',
      }
    ],
    instagram: [
      {
        name: 'networkPageName',
        hidden: undefined,
        sortable: PageSortValue.NetworkPageName,
        label: 'networkPageName',
      },
      {
        name: 'networkPageId',
        hidden: undefined,
        sortable: PageSortValue.NetworkPageId,
        label: 'networkPageId',
      },
      {
        name: 'active',
        hidden: HiddenAttr.mdUp,
        sortable: PageSortValue.Active,
        label: 'active',
      },
      {
        name: 'firstConnectedAt',
        hidden: HiddenAttr.mdUp,
        sortable: PageSortValue.FirstConnectedAt,
        label: 'firstConnectedAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'lastConnectedAt',
        hidden: HiddenAttr.mdUp,
        sortable: PageSortValue.LastConnectedAt,
        label: 'lastConnectedAt',
        format: `${defaultDateFormat}, ${defaultTimeFormat}`,
      },
      {
        name: 'pageTokenValid',
        hidden: HiddenAttr.lgUp,
        sortable: PageSortValue.PageTokenValid,
        label: 'pageTokenValid',
      },
      {
        name: 'deleteAction',
        hidden: undefined,
      },
      {
        name: 'urlToPage',
        hidden: undefined,
        label: 'urlToPage',
      }
    ],
    google: [
      {
        name: 'networkPageName',
        hidden: undefined,
        sortable: PageSortValue.NetworkPageName,
        label: 'networkPageName',
      },
      {
        name: 'networkPageId',
        hidden: HiddenAttr.smUp,
        sortable: PageSortValue.NetworkPageId,
        label: 'networkPageId',
      },
      {
        name: 'storeCode',
        hidden: HiddenAttr.mdUp,
        sortable: undefined,
        label: 'storeCode',
      },
      {
        name: 'deleteAction',
        hidden: undefined,
      },
    ],
  }

  const handleSort = (column: string) => {
    const isAsc = sortColumn === column && sortOrder === 'asc'
    setSortOrder(isAsc ? 'desc' : 'asc')
    setSortColumn(column)
  }

  const sortedPages = () => {
    const pages = selectedNetwork === 'facebook' ? networkPagesData?.facebookPages
      : selectedNetwork === 'instagram' ? networkPagesData?.instagramPages
        : networkPagesData?.googlePages

    if (!pages) return []

    // Helper function to normalize different types into strings for comparison
    const getSortableValue = (value: unknown): string => {
      if (typeof value === 'boolean') return value ? '1' : '0' // Convert boolean to '1' or '0'
      if (typeof value === 'number') return value.toString() // Convert number to string
      if (typeof value === 'string') return value // Return string as is
      return '' // Return empty string for unsupported types or undefined
    }

    return [...pages].sort((a, b) => {
      // Extract and normalize values for comparison
      const aValue = getSortableValue(a[sortColumn as keyof typeof a])
      const bValue = getSortableValue(b[sortColumn as keyof typeof b])

      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1
      } else if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1
      } else {
        return 0
      }
    })
  }

  // Render table rows based on the pages data
  const renderTableRows = () => {
    const pages = sortedPages()

    return pages.map((row: (FacebookPage | InstagramPage | GooglePage), index: number) => (
      <TableRow key={`${index}-${row.networkPageId}`}>
        <TableCell>{row.networkPageName}</TableCell>
        <TableCell>{row.networkPageId}</TableCell>
        <TableCell>{'active' in row ? (row.active ? t('Aktiv') : t('Inaktiv')) : '-'}</TableCell>
        <TableCell>{'firstConnectedAt' in row ? row.firstConnectedAt : '-'}</TableCell>
        <TableCell>{'lastConnectedAt' in row ? row.lastConnectedAt : '-'}</TableCell>
        <TableCell>{'pageTokenValid' in row ? (row.pageTokenValid ? t('Gültig') : t('Ungültig')) : '-'}</TableCell>
        <TableCell>
          <IconButton
            onClick={() => {
              setPageToDelete({ name: row.networkPageName, id: row.networkPageId })
              setDeleteModalOpen(true)
            }}
            color="primary"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
        <TableCell>
          {/* Conditionally render the button based on the selected network */}
          {selectedNetwork === 'facebook' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.open(`https://www.facebook.com/${row.networkPageId}`, '_blank')}
              disableElevation
              fullWidth
            >
              {t('Zur Facebook-Seite')}
            </Button>
          )}
          {selectedNetwork === 'instagram' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.open(`https://www.instagram.com/${row.networkPageName}`, '_blank')}
              disableElevation
            >
              {t('Zur Instagram-Seite')}
            </Button>
          )}
          {/* No button for Google pages */}
        </TableCell>
      </TableRow>
    ))
  }

  const handleCloseModal = () => {
    setDeleteModalOpen(false)
    setPageToDelete(null)
  }

  // Map frontend network type to backend NetworkType enum
  const getBackendNetworkType = (network: keyof PageTableTemplate): string => {
    switch (network) {
    case 'facebook':
      return 'Facebook'
    case 'instagram':
      return 'Instagram'
    case 'google':
      return 'GoogleMyBusiness'
    default:
      return 'Facebook'
    }
  }

  const handleConfirmDelete = async () => {
    if (!pageToDelete) return

    const networkType = getBackendNetworkType(selectedNetwork)
    const restUrl = `${BASE_PATH}/rest/professocial/core/admin/removeUnlinkedPage?networkType=${networkType}&networkPageId=${encodeURIComponent(pageToDelete.id)}`

    try {
      const response = await fetch(restUrl, {
        method: 'DELETE',
        credentials: 'include',
        headers: requestHeaders,
      })

      if (response.ok) {
        // Success: remove the deleted page from local state instead of refetching
        if (networkPagesData) {
          const updatedData = { ...networkPagesData }
          
          if (selectedNetwork === 'facebook') {
            updatedData.facebookPages = updatedData.facebookPages.filter(
              page => page.networkPageId !== pageToDelete.id
            )
          } else if (selectedNetwork === 'instagram') {
            updatedData.instagramPages = updatedData.instagramPages.filter(
              page => page.networkPageId !== pageToDelete.id
            )
          } else if (selectedNetwork === 'google') {
            updatedData.googlePages = updatedData.googlePages.filter(
              page => page.networkPageId !== pageToDelete.id
            )
          }
          
          setNetworkPagesData(updatedData)
        }
        handleCloseModal()
      } else {
        console.error('Failed to delete page:', response.statusText)
        alert(`Fehler beim Löschen der Seite: ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error deleting page:', error)
      alert('Fehler beim Löschen der Seite. Bitte versuchen Sie es erneut.')
    }
  }

  return (
    <>
      <Grid container style={{ margin: '25px 0px' }}>
        <Grid item xs={12}>
          {/* Dropdown for selecting network */}
          <Grid xs={3} sx={{ mb: 3 }} item container direction="row">
            <DropdownComponent
              options={filterOptions}
              value={selectedNetwork}
              label={t('accountsCommon:network')}
              size="small"
              disableClearable
              onChange={(e) => handleNetworkChange(e)}
            />
          </Grid>

          {/* Table for displaying network pages data */}
          <Paper sx={{ py: 4, px: 3, mb: 7 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {pageTableTemplate[selectedNetwork].map((element) => {
                        return (
                          <TableCell
                            className={classes.tableHeadCell}
                            size="medium"
                            align="left"
                            key={`head-${element.name}`}
                            sx={{ display: isVisible(element.hidden) ? 'table-cell' : 'none' }}
                          >
                            <Tooltip title="Sort" enterDelay={300}>
                              <TableSortLabel
                                active={orderBy === `${element.name}`}
                                direction={order}
                                className={classes.sortLabel}
                                onClick={() => {
                                  handleSort(element.name)
                                }}
                              >
                                {element.label && t<string>(`accountsCommon:${element.label}`)}
                              </TableSortLabel>
                            </Tooltip>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renderTableRows()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Seite löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Wollen Sie die Seite &quot;{pageToDelete?.name}&quot; inklusiver aller Statistiken endgültig löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleConfirmDelete} color="primary" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}


export default UnlinkedPagesTable
